import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getQuerySchema, parseTextAPI, executeQueryWithAST } from '../../utils/api/query_api';
import { STATUS } from '../../utils/api/sharedfunction';
import { validateFilterPill, ensureFilterPillId } from '../../utils/query/filterPillValidation';
import { syncUIToAdvanced, syncAdvancedToUI, canTransitionMode } from '../../utils/query/modeSync';
import { astToText } from '../../utils/query/astToText';

/**
 * Redux slice for query state management
 * 
 * Phase 8: Full query system with filter pills, AST, and mode switching
 */

const initialState = {
  schema: null,
  schemaLoading: false,
  schemaError: null,
  entity: 'testcase',
  mode: 'ui',  // 'ui' | 'advanced' (removed 'advanced-readonly')
  filterPills: [],  // Array<FilterPill>
  quickFilter: '',
  ast: null,  // Canonical AST
  isComplex: false,  // Whether current AST is too complex for UI mode
  inputText: '',  // Query text for advanced mode
  isParsing: false,  // Parsing state (separate from execution)
  parseError: null,  // Parse error state
  results: [],
  resultsLoading: false,  // Execution state (separate from parsing)
  resultsError: null,
  executionMeta: null,
  filterPillError: null,  // Validation error state
  // Sorting state (persisted across page refreshes)
  sortField: 'created_at',  // Field to sort by
  sortOrder: 'desc'  // Sort direction: 'asc' | 'desc'
};

/**
 * Async thunk to fetch query schema from backend
 */
export const fetchSchema = createAsyncThunk(
  'query/fetchSchema',
  async ({ entity = 'testcase', projectId = null } = {}, { rejectWithValue }) => {
    try {
      const result = await getQuerySchema(entity, projectId);
      if (result.status === STATUS.SUCCESS && result.data) {
        return result.data;
      }
      return rejectWithValue(result.error || { message: 'Failed to fetch schema' });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch schema');
    }
  }
);

/**
 * Add filter pill (with validation)
 */
export const addFilterPill = createAsyncThunk(
  'query/addFilterPill',
  async (pill, { getState, rejectWithValue }) => {
    try {
      const state = getState().query;
      // Normalization happens in filterPillsToAST, not here
      // Validate and ensure ID on the pill as-is
      validateFilterPill(pill);
      const pillWithId = ensureFilterPillId(pill);
      return pillWithId;
    } catch (error) {
      return rejectWithValue({ error: error.message, pill });
    }
  }
);

/**
 * Update filter pill (with validation)
 */
export const updateFilterPill = createAsyncThunk(
  'query/updateFilterPill',
  async ({ id, updates }, { getState, rejectWithValue }) => {
    try {
      const state = getState().query;
      const existingPill = state.filterPills.find(p => p.id === id);
      if (!existingPill) {
        return rejectWithValue({ error: 'Filter pill not found', id });
      }
      
      const updated = { ...existingPill, ...updates };
      // Normalization happens in filterPillsToAST, not here
      validateFilterPill(updated);
      return { id, pill: updated };
    } catch (error) {
      return rejectWithValue({ error: error.message, id, updates });
    }
  }
);

/**
 * Remove filter pill
 */
export const removeFilterPill = createAsyncThunk(
  'query/removeFilterPill',
  async (id) => {
    return id;
  }
);

/**
 * Reorder filter pills
 */
export const reorderFilterPills = createAsyncThunk(
  'query/reorderFilterPills',
  async (ids) => {
    return ids;
  }
);

/**
 * Parse text to AST
 */
export const parseText = createAsyncThunk(
  'query/parseText',
  async ({ projectId, text }, { getState, rejectWithValue }) => {
    try {
      const state = getState().query;
      const schema = state.schema;
      const result = await parseTextAPI(projectId, text, schema);
      if (result.status === STATUS.SUCCESS && result.data) {
        return result.data.ast;
      }
      return rejectWithValue(result.error || { message: 'Failed to parse query' });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to parse query');
    }
  }
);

/**
 * Execute query (AST-only)
 */
export const executeQuery = createAsyncThunk(
  'query/executeQuery',
  async ({ projectId, ast, page, pageSize, sortField, sortOrder, moduleId = 'all' }, { getState, rejectWithValue }) => {
    try {
      const state = getState().query;
      const schema = state.schema;
      const result = await executeQueryWithAST(projectId, { ast, page, pageSize, sortField, sortOrder, moduleId, schema });
      if (result.status === STATUS.SUCCESS && result.data) {
        return result.data;
      }
      return rejectWithValue(result.error || { message: 'Query execution failed' });
    } catch (error) {
      return rejectWithValue(error.message || 'Query execution failed');
    }
  }
);

/**
 * Change mode (with transition validation)
 */
export const changeMode = createAsyncThunk(
  'query/changeMode',
  async ({ mode, projectId }, { getState, rejectWithValue }) => {
    const state = getState().query;
    const { allowed, reason } = canTransitionMode(state.mode, mode, state.isComplex);
    
    if (!allowed) {
      return rejectWithValue({ error: reason || 'Mode transition not allowed', fromMode: state.mode, toMode: mode });
    }
    
    // Perform sync based on transition
    if (state.mode === 'ui' && mode === 'advanced') {
      // UI → Advanced: pills → AST → text
      // Pass schema to format user fields correctly (show usernames instead of IDs)
      const { ast, text } = syncUIToAdvanced(state.filterPills || [], state.quickFilter || '', state.schema || null);
      return { mode, ast, text, isComplex: false };
    } else if (state.mode === 'advanced' && mode === 'ui') {
      // Advanced → UI: text → AST → pills
      // If AST is not available, try to parse text, or use empty state
      let astToUse = state.ast;
      
      if (!astToUse && state.inputText && state.inputText.trim()) {
        // If there's text but no AST, treat it as complex (can't convert)
        return {
          mode: 'advanced', // Stay in advanced mode
          isComplex: true,
          filterPills: [],
          quickFilter: '',
          warning: 'Query text exists but cannot be converted to UI mode. Please clear the query first.'
        };
      }
      
      // If no AST and no text, return to empty UI state
      if (!astToUse) {
        return {
          mode: 'ui',
          ast: null, // Explicitly clear AST
          isComplex: false,
          filterPills: [],
          quickFilter: '',
          warning: null
        };
      }
      
      // Pass schema to convert user IDs to user objects for display
      const { filterPills, quickFilter, isComplex, warning } = syncAdvancedToUI(astToUse, state.schema || null);
      // If complex, stay in advanced mode but set isComplex flag
      if (isComplex) {
        // Stay in advanced mode - keep AST
        return { 
          mode: 'advanced', 
          filterPills, 
          quickFilter, 
          isComplex,
          warning 
        };
      } else {
        // Switch to UI mode - clear AST (pills are the source of truth)
        return { 
          mode: 'ui', 
          ast: null, // Clear AST when converting to UI mode (pills are the source of truth)
          filterPills, 
          quickFilter, 
          isComplex,
          warning 
        };
      }
    }
    
    // Other transitions (staying in same mode, etc.)
    return { mode };
  }
);

const querySlice = createSlice({
  name: 'query',
  initialState,
  reducers: {
    /**
     * Change entity (triggers schema refetch if needed)
     */
    setEntity: (state, action) => {
      const newEntity = action.payload || 'testcase';
      if (state.entity !== newEntity) {
        state.entity = newEntity;
        // Clear schema when entity changes (will trigger refetch)
        state.schema = null;
        state.schemaError = null;
      }
    },
    /**
     * Update query text
     */
    setText: (state, action) => {
      state.inputText = action.payload || '';
    },
    /**
     * Set filter pills (bulk set, ensure all have ids)
     */
    setFilterPills: (state, action) => {
      state.filterPills = action.payload.map(pill => ensureFilterPillId(pill));
      state.filterPillError = null;
    },
    /**
     * Set quick filter
     */
    setQuickFilter: (state, action) => {
      state.quickFilter = action.payload || '';
    },
    /**
     * Set AST directly (and update isComplex flag)
     */
    setAST: (state, action) => {
      state.ast = action.payload;
      // Update isComplex flag when AST changes
      if (action.payload) {
        const { astToFilterPills } = require('../../utils/query/astToFilterPills');
        const { isComplex } = astToFilterPills(action.payload);
        state.isComplex = isComplex;
      } else {
        state.isComplex = false;
      }
    },
    /**
     * Set isComplex flag directly
     */
    setIsComplex: (state, action) => {
      state.isComplex = action.payload;
    },
    /**
     * Set mode (no enforcement needed - UI will disable button if isComplex)
     */
    setMode: (state, action) => {
      state.mode = action.payload;
    },
    /**
     * Clear results
     */
    clearResults: (state) => {
      state.results = [];
      state.resultsLoading = false;
      state.resultsError = null;
      state.executionMeta = null;
    },
    /**
     * Clear parse error
     */
    clearParseError: (state) => {
      state.isParsing = false;
      state.parseError = null;
    },
    /**
     * Clear filter pill error
     */
    clearFilterPillError: (state) => {
      state.filterPillError = null;
    },
    /**
     * Set sort field
     */
    setSortField: (state, action) => {
      state.sortField = action.payload || 'created_at';
    },
    /**
     * Set sort order
     */
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload || 'desc';
    },
    /**
     * Set sort (field and order together)
     */
    setSort: (state, action) => {
      const { field, order } = action.payload;
      if (field !== undefined) {
        state.sortField = field || 'created_at';
      }
      if (order !== undefined) {
        state.sortOrder = order || 'desc';
      }
    },
    /**
     * Clear errors
     */
    clearError: (state) => {
      state.schemaError = null;
    },
    /**
     * Reset query state
     */
    reset: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchSchema
      .addCase(fetchSchema.pending, (state) => {
        state.schemaLoading = true;
        state.schemaError = null;
      })
      .addCase(fetchSchema.fulfilled, (state, action) => {
        state.schemaLoading = false;
        state.schema = action.payload;
        state.schemaError = null;
      })
      .addCase(fetchSchema.rejected, (state, action) => {
        state.schemaLoading = false;
        state.schemaError = action.payload || { message: 'Failed to fetch schema' };
      })
      
      // addFilterPill
      .addCase(addFilterPill.fulfilled, (state, action) => {
        state.filterPills.push(action.payload);
        state.filterPillError = null;
      })
      .addCase(addFilterPill.rejected, (state, action) => {
        state.filterPillError = action.payload;
      })
      
      // updateFilterPill
      .addCase(updateFilterPill.fulfilled, (state, action) => {
        const { id, pill } = action.payload;
        const index = state.filterPills.findIndex(p => p.id === id);
        if (index !== -1) {
          state.filterPills[index] = pill;
        }
        state.filterPillError = null;
      })
      .addCase(updateFilterPill.rejected, (state, action) => {
        state.filterPillError = action.payload;
      })
      
      // removeFilterPill
      .addCase(removeFilterPill.fulfilled, (state, action) => {
        state.filterPills = state.filterPills.filter(p => p.id !== action.payload);
      })
      
      // reorderFilterPills
      .addCase(reorderFilterPills.fulfilled, (state, action) => {
        const ids = action.payload;
        const idMap = new Map(state.filterPills.map(p => [p.id, p]));
        state.filterPills = ids.map(id => idMap.get(id)).filter(Boolean);
      })
      
      // parseText (separate from execution)
      .addCase(parseText.pending, (state) => {
        state.isParsing = true;
        state.parseError = null;
      })
      .addCase(parseText.fulfilled, (state, action) => {
        state.ast = action.payload;
        // Format AST to text with schema for user field formatting
        state.inputText = astToText(action.payload, { schema: state.schema || null });
        state.isParsing = false;
        state.parseError = null;
        // Update isComplex flag when AST is parsed
        // Pass schema to properly detect date fields for multiple pill detection
        if (action.payload) {
          const { astToFilterPills } = require('../../utils/query/astToFilterPills');
          const { isComplex } = astToFilterPills(action.payload, state.schema || null);
          state.isComplex = isComplex;
        } else {
          state.isComplex = false;
        }
      })
      .addCase(parseText.rejected, (state, action) => {
        // Normalize error structure for consistent display
        const error = action.payload;
        state.parseError = {
          message: error?.message || error?.error || 'Failed to parse query',
          code: error?.code,
          line: error?.line,
          column: error?.column,
          context: error?.context
        };
        state.isParsing = false;
      })
      
      // executeQuery (separate from parsing)
      .addCase(executeQuery.pending, (state) => {
        state.resultsLoading = true;
        state.resultsError = null;
      })
      .addCase(executeQuery.fulfilled, (state, action) => {
        state.results = action.payload.rows || [];
        state.executionMeta = action.payload.meta || null;
        state.resultsLoading = false;
      })
      .addCase(executeQuery.rejected, (state, action) => {
        state.resultsError = action.payload;
        state.resultsLoading = false;
      })
      
      // changeMode
      .addCase(changeMode.fulfilled, (state, action) => {
        const { mode, ast, text, filterPills, quickFilter, isComplex, warning } = action.payload;
        state.mode = mode;
        if (ast !== undefined) state.ast = ast;
        if (text !== undefined) state.inputText = text;
        if (filterPills !== undefined) state.filterPills = filterPills;
        if (quickFilter !== undefined) state.quickFilter = quickFilter;
        if (isComplex !== undefined) state.isComplex = isComplex;
        // Warning can be displayed in UI component
      })
      .addCase(changeMode.rejected, (state, action) => {
        // Mode transition failed - show error
        // State remains unchanged
      });
  }
});

export const {
  setEntity,
  setText,
  setFilterPills,
  setQuickFilter,
  setAST,
  setIsComplex,
  setMode,
  clearResults,
  clearParseError,
  clearFilterPillError,
  setSortField,
  setSortOrder,
  setSort,
  clearError,
  reset
} = querySlice.actions;

export default querySlice.reducer;

